/**
 * Server-side dataset import.
 *
 * The page uploads its XML files, then hands the server a spec and stops being responsible: the import
 * runs off the durable script-job queue, so the browser can close. Progress is read back from the run
 * aggregate rather than held in the tab, which is also what lets a reload rejoin an import in flight.
 */
import { apiEndpoints } from '@config/env';
import { getData, postData } from '@utils/global';
import { debug, warn } from '@utils/console';

export type ImportRunMode = 'race_day' | 'training_day';

/** Mirrors the phases the server enqueues, in order. */
export type ImportRunPhase =
  | 'parse'
  | 'normalize'
  | 'datasets'
  | 'report_names'
  | 'markwind'
  | 'process'
  | 'descriptions'
  | 'cleanup'
  | 'finalize';

export type ImportRunState = 'active' | 'succeeded' | 'succeeded_with_warnings';

export interface ImportRunConfiguration {
  name: string;
  headsail: string;
  crew: string;
  wing: string;
  rudder: string;
  daggerboard: string;
}

export interface StartImportRunSpec {
  idempotency_key: string;
  mode: ImportRunMode;
  class_name: string;
  project_id: number | string;
  /**
   * YYYY-MM-DD or YYYYMMDD. Typed by the user, or extracted from the race course files at selection
   * time when they carry a readable timestamp.
   */
  dates: string[];
  source_names: string[];
  event_name?: string;
  timezone: string;
  target_name?: string;
  configuration?: Partial<ImportRunConfiguration>;
}

export interface ImportRunSummary {
  import_run_id: string;
  mode: ImportRunMode;
  project_id: string;
  class_name: string;
  state: ImportRunState;
  phase: ImportRunPhase | null;
  label: string | null;
  /** 2-4, matching the upload page's existing step display. Step 1 is the client-side upload. */
  ui_step: number;
  steps_total: number;
  steps_done: number;
  steps_failed: number;
  started_at?: string;
  updated_at?: string;
}

export interface ImportRunStep {
  id: string;
  script_name: string;
  status: string;
  failure_mode?: string | null;
  error_message?: string | null;
  phase: ImportRunPhase;
  label: string;
  seq: number;
  output_summary?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface ImportRunDetail extends ImportRunSummary {
  steps: ImportRunStep[];
}

export interface StartImportRunResult {
  import_run_id: string;
  steps_total?: number;
  dates?: string[];
  sources?: string[];
  /** True when this call matched an existing idempotency key instead of starting a new import. */
  replayed?: boolean;
}

/** Generate the per-click key that stops a double submit starting two imports. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `import-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Start an import. Resolves once the plan is queued — not when the import finishes.
 *
 * Throws with the server's message on refusal. A 409 means another import is already running for the
 * project; its run id is on the thrown error as `activeRunId`.
 */
export async function startImportRun(spec: StartImportRunSpec): Promise<StartImportRunResult> {
  const response = await postData(apiEndpoints.admin.import_runs, spec as unknown as Record<string, unknown>);
  if (!response?.success || !response?.data?.import_run_id) {
    const error = new Error(response?.message || 'Could not start the import') as Error & {
      status?: number;
      activeRunId?: string;
    };
    error.status = response?.status;
    error.activeRunId = response?.data?.active_run_id;
    throw error;
  }
  debug('[importRuns] started', response.data);
  return response.data as StartImportRunResult;
}

/**
 * Imports with work still queued or running, newest first.
 *
 * Open to any authenticated user: it backs the global "data loading in progress" banner, so everyone
 * needs to be able to see that the project's data is still being built.
 *
 * Returns `null` when the question could not be asked — a dropped request, a cancelled one, a server
 * that did not answer. That is NOT the same answer as an empty array, and conflating the two let one
 * blip on the 30s banner poll clear a banner for an import that was still running. Callers that only
 * display runs can treat null as empty; callers holding state must keep what they had.
 *
 * `bestEffort`: for the background poll, whose failures are invisible and must not be logged as
 * application errors. Leave it off for a user-initiated read, where a failure is worth seeing.
 */
export async function getActiveImportRuns(
  projectId?: number | string,
  options: { signal?: AbortSignal; bestEffort?: boolean } = {}
): Promise<ImportRunSummary[] | null> {
  const url = projectId != null && String(projectId).trim() !== ''
    ? `${apiEndpoints.admin.import_runs_active}?project_id=${encodeURIComponent(projectId)}`
    : apiEndpoints.admin.import_runs_active;
  try {
    const response = await getData(url, options.signal, { bestEffort: options.bestEffort });
    if (!response?.success) {
      if (!options.bestEffort) warn('[importRuns] could not read active runs:', response?.message || response?.error);
      return null;
    }
    return (response.data?.runs ?? []) as ImportRunSummary[];
  } catch (e) {
    if (!options.bestEffort) warn('[importRuns] could not read active runs:', e);
    return null;
  }
}

/** One run with its ordered steps, or null when it no longer exists. */
export async function getImportRun(importRunId: string): Promise<ImportRunDetail | null> {
  try {
    const response = await getData(apiEndpoints.admin.import_run(importRunId));
    if (!response?.success || !response?.data) return null;
    return response.data as ImportRunDetail;
  } catch (e) {
    warn('[importRuns] could not read run:', e);
    return null;
  }
}

/**
 * Cancel a run's queued steps and ask the running one to stop.
 *
 * Datasets already created stay — the same outcome closing the tab mid-import used to give.
 */
export async function cancelImportRun(importRunId: string): Promise<number> {
  const response = await postData(apiEndpoints.admin.import_run_cancel(importRunId), {});
  if (!response?.success) {
    throw new Error(response?.message || 'Could not cancel the import');
  }
  return response.data?.canceled_count ?? 0;
}

/** One line of human-readable progress, e.g. "Normalize GER 2026-03-01 (12 of 27)". */
export function describeImportRun(run: ImportRunSummary | null | undefined): string {
  if (!run) return '';
  const label = run.label || run.phase || 'Working';
  const counts = run.steps_total > 0 ? ` (${run.steps_done} of ${run.steps_total})` : '';
  const failed = run.steps_failed > 0
    ? ` — ${run.steps_failed} step${run.steps_failed === 1 ? '' : 's'} need attention`
    : '';
  return `${label}${counts}${failed}`;
}
